import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {IElementQuestionMatchPair, MatchPairQuestionMode} from '../../../../../incase-domain/elements/question/questionMatchPair';
import {QuestionMatchPairInteractions} from '../../../../../incase-domain/proptypes/runfile';
import {pointsSelector} from '../../../../../store/selectors/runfile.selectors';
import {checkIfPairIsCorrect} from '../../../../../util/matchPairs.utils';
import MatchPairsQuestionElementItem from './MatchPairQuestionElements';
import MatchPairQuestionCards from './MatchPairQuestionCards';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import {getNewPoints} from '../../../../../service/gamification.service';
import {IGameConfig} from '../../../../../incase-domain/proptypes/gameConfig';
import {gameConfigSelector} from '../../../../../store/selectors/presentation.selectors';
import QuestionLayout from '../common/QuestionLayout';
import useMatchPairTry from './useMatchPairTry';
import {handleElementsClickedAction} from '../../../../../store/actions/chalkBoardElements.actions';

export interface Pair {
    leftSelectedElement: string;
    rightSelectedElement: string;
}

interface MatchPairQuestionElementProps {
    element: IElementQuestionMatchPair;
    elementInteractions: QuestionMatchPairInteractions;
    savePresentationRunFile: (elementRunFile: Record<string, unknown>, newPoints: number) => void;
    device?: DeviceType;
}

function MatchPairQuestionElement(props: MatchPairQuestionElementProps): JSX.Element {
    const {element, elementInteractions, savePresentationRunFile, device} = props;
    const {haveTryButton} = element;
    const {submittedPairs, answered} = elementInteractions;
    const gameConfig = useSelector(gameConfigSelector);
    const dispatch = useDispatch();

    const points = useSelector(pointsSelector);

    const matchPairsTry = useMatchPairTry();
    const PairItems = element.matchPairMode === MatchPairQuestionMode.CARDS ? MatchPairQuestionCards : MatchPairsQuestionElementItem;

    const [wrongAnswer, setWrongAnswer] = useState<Pair>({leftSelectedElement: '', rightSelectedElement: ''});

    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout>;
        if (wrongAnswer.leftSelectedElement && wrongAnswer.rightSelectedElement) {
            timeout = setTimeout(() => {
                setWrongAnswer({leftSelectedElement: '', rightSelectedElement: ''});
            }, 1000);
        }
        return () => clearTimeout(timeout);
    }, [wrongAnswer]);

    const getPoints = (isCorrect: boolean): number => {
        return getNewPoints(points || 0, isCorrect, gameConfig || ({} as IGameConfig), element.gameConfig);
    };

    const handleClickPairItem = (id: string, position: keyof Pair): void => {
        const updatedPairs = [...(submittedPairs || [])];
        let currentPoints = points || 0;

        // Find all existing pairs containing this item
        const existingPairIndices = updatedPairs.reduce<number[]>((acc, pair, index) => {
            if (pair.leftSelectedElement === id) {
                acc.push(index);
            }
            return acc;
        }, []);

        // Remove existing pairs if item is already selected
        if (existingPairIndices.length > 0) {
            existingPairIndices
                .sort((a, b) => b - a)
                .forEach((index) => {
                    updatedPairs.splice(index, 1);
                });
        } else {
            // Find half-complete pairs
            const halfPairIndices = updatedPairs.reduce<number[]>((acc, pair, index) => {
                if (!pair.leftSelectedElement !== !pair.rightSelectedElement) {
                    acc.push(index);
                }
                return acc;
            }, []);

            if (halfPairIndices.length > 0) {
                halfPairIndices.forEach((halfPairIndex) => {
                    const halfPair = {...updatedPairs[halfPairIndex]};
                    halfPair[position] = id;

                    const isPair = halfPair.leftSelectedElement && halfPair.rightSelectedElement;
                    const isCorrect = checkIfPairIsCorrect(element, halfPair.leftSelectedElement, halfPair.rightSelectedElement);

                    if (!haveTryButton && isPair) {
                        currentPoints = getPoints(isCorrect);

                        if (isCorrect) {
                            updatedPairs[halfPairIndex] = halfPair;
                        } else {
                            setWrongAnswer(halfPair);
                            updatedPairs.splice(halfPairIndex, 1);
                        }
                    } else {
                        updatedPairs[halfPairIndex] = halfPair;
                    }
                });
            } else {
                // Create a new partial pair
                const newPair: Pair = {leftSelectedElement: '', rightSelectedElement: ''};
                newPair[position] = id;
                updatedPairs.push(newPair);
            }
        }

        handleUpdate(updatedPairs, currentPoints);
    };

    const handleUpdate = (updatedPairs: Pair[], points: number): void => {
        const isAnswered =
            updatedPairs.length === element.pairs.length &&
            updatedPairs.every((pair) => !!pair.leftSelectedElement && !!pair.rightSelectedElement);
        savePresentationRunFile(
            {
                ...elementInteractions,
                submittedPairs: updatedPairs,
                answered: haveTryButton ? false : isAnswered,
            },
            points
        );

        if (isAnswered && !element.haveTryButton) dispatch(handleElementsClickedAction([element.id], !!element.clearAfterClicked));
    };

    return (
        <QuestionLayout
            element={element}
            elementInteractions={elementInteractions}
            device={device}
            showConfirmButton={haveTryButton}
            onConfirm={() => matchPairsTry(element, elementInteractions)}
            confirmDisabled={
                answered ||
                !submittedPairs ||
                submittedPairs?.length < element.pairs.length ||
                submittedPairs.some((pair) => !pair.leftSelectedElement || !pair.rightSelectedElement)
            }
        >
            <PairItems
                element={element}
                selectedPairs={submittedPairs || []}
                answered={answered}
                wrongAnswer={wrongAnswer}
                handleItemClick={handleClickPairItem}
                device={device}
            />
        </QuestionLayout>
    );
}

export default MatchPairQuestionElement;
